import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { shareReplay, throttleTime } from 'rxjs/operators';
import { DriverDetail, LoHiRecommendedShiftDetail, RecommendedShiftBucket, RecommendedShiftRegion } from '../types';

@Injectable({
  providedIn: 'root',
})
export class ShiftBookingService {
  private shiftRegions$$ = new BehaviorSubject<RecommendedShiftRegion[]>([]);
  private shiftRegionsThrottle$$ = new Subject();
  private shiftRegionsShared$ = this.shiftRegions$$.pipe(shareReplay(1));
  public get shiftRegions$(): Observable<RecommendedShiftRegion[]> {
    this.shiftRegionsThrottle$$.next();
    return this.shiftRegionsShared$;
  }

  constructor(private httpClient: HttpClient, private logger: NGXLogger) {
    this.shiftRegionsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadShiftRegions();
    });
  }

  public async loadTimesForRecommendation(recommendationId: number): Promise<RecommendedShiftBucket[]> {
    try {
      const result = await this.httpClient
        .get<RecommendedShiftBucket[]>(`${environment.api}/v1/user/recommended_shift_time_buckets`, {
          params: {
            recommendationId: `${recommendationId}`,
          },
        })
        .toPromise();
      return result;
    } catch (error) {
      this.logger.error(error);
      return Promise.reject('Failed to load shift times');
    }
  }

  public async loadRecommendationDetail(
    recommendedShiftId: number,
    requestedStartTime: string,
  ): Promise<LoHiRecommendedShiftDetail> {
    try {
      const result = await this.httpClient
        .post<LoHiRecommendedShiftDetail>(`${environment.api}/v1/admin/shift/create_custom_recommendation_from_id`, {
          recommendedShiftId,
          requestedStartTime,
        })
        .toPromise();
      return result;
    } catch (error) {
      this.logger.error(error);
      return Promise.reject('Failed to load drivers');
    }
  }

  public async loadDriversForRecommendation(recommendationId: number): Promise<DriverDetail[]> {
    try {
      const result = await this.httpClient
        .get<DriverDetail[]>(`${environment.api}/v1/admin/recommended_shift/${recommendationId}/eligible_users`, {
          params: {
            recommendationId: `${recommendationId}`,
          },
        })
        .toPromise();
      return result;
    } catch (error) {
      this.logger.error(error);
      return Promise.reject('Failed to load drivers');
    }
  }

  public async bookShiftForDriver(recommendationId: number, phoneNumber: string): Promise<boolean> {
    try {
      await this.httpClient
        .post<null>(
          `${environment.api}/v1/admin/recommended_shift/${recommendationId}/book`,
          {
            phoneNumber,
          },
          {
            params: {
              source: 'sandi',
            },
          },
        )
        .toPromise();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  private async loadShiftRegions() {
    try {
      const result = await this.httpClient
        .get<RecommendedShiftRegion[]>(`${environment.api}/v1/user/recommended_shift_regions`)
        .toPromise();
      this.shiftRegions$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }
}
